<template>
  <div class="app-layout">
    <Sidebar @toggle-sidebar="toggleSidebar" :class="{ 'sidebar-open': isSidebarOpen }" />
    <div class="main-content">
      <div class="sessions-container">
        <div class="page-header">
          <div class="header-content">
            <div class="title-wrapper">
              <h1 class="page-title">Sessions</h1>
              <div class="stats-badge">{{ filteredSessions.length }} total</div>
            </div>
            <p class="page-subtitle">Manage and view all your sessions</p>
          </div>
        </div>

        <!-- Statistics Section -->
        <div class="statistics-section">
          <div class="stats-grid">
            <!-- Sessions Stats -->
            <div class="stats-card sessions">
              <div class="stats-header">
                <h3>Session Statistics</h3>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="stats-icon">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div class="stats-content">
                <div class="stat-item">
                  <span class="stat-label">Total Sessions</span>
                  <span class="stat-value">{{ statistics.total_sessions }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">This Month</span>
                  <span class="stat-value">{{ statistics.this_month_sessions }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Upcoming</span>
                  <span class="stat-value">{{ statistics.upcoming_sessions }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Cancelled</span>
                  <span class="stat-value">{{ statistics.cancelled_sessions }}</span>
                </div>
              </div>
            </div>

            <!-- Status Stats -->
            <div class="stats-card status">
              <div class="stats-header">
                <h3>Session Status</h3>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="stats-icon">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <div class="stats-content">
                <div class="stat-item">
                  <span class="stat-label">Completed</span>
                  <span class="stat-value">{{ statistics.completed_sessions }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Completion Rate</span>
                  <span class="stat-value">{{ ((statistics.completed_sessions / statistics.total_sessions) * 100 || 0).toFixed(1) }}%</span>
                </div>
              </div>
            </div>

            <!-- Revenue Stats -->
            <div class="stats-card revenue">
              <div class="stats-header">
                <h3>Revenue</h3>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="stats-icon">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <div class="stats-content">
                <div class="stat-item">
                  <span class="stat-label">Average Price</span>
                  <span class="stat-value">${{ Number(statistics.average_session_price).toFixed(2) }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Est. Monthly Revenue</span>
                  <span class="stat-value">${{ (Number(statistics.average_session_price) * statistics.this_month_sessions).toFixed(2) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Search and Filters -->
        <div class="search-and-filters">
          <div class="search-box">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search sessions..."
              class="search-input"
            >
          </div>
          <div class="filter-controls">
            <select v-model="filters.status" class="filter-select">
              <option value="">Status: All</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select v-model="filters.type" class="filter-select">
              <option value="">Type: All</option>
              <option value="one-on-one">One-on-One</option>
              <option value="group">Group</option>
            </select>
            <select v-model="filters.duration" class="filter-select">
              <option value="">Duration: All</option>
              <option value="30">30 minutes</option>
              <option value="60">60 minutes</option>
              <option value="90">90 minutes</option>
            </select>
          </div>
        </div>

        <!-- Sessions Table -->
        <div class="content-card">
          <div class="table-header">
            <div class="table-title">All Sessions</div>
          </div>
          <div class="table-responsive">
            <table class="sessions-table">
              <thead>
                <tr>
                  <th @click="sortBy('id')" :class="{ sortable: true, sorted: sortKey === 'id', 'sort-asc': sortOrder === 'asc' && sortKey === 'id', 'sort-desc': sortOrder === 'desc' && sortKey === 'id' }">
                    ID
                    <span class="sort-icon"></span>
                  </th>
                  <th @click="sortBy('user')" :class="{ sortable: true, sorted: sortKey === 'user' }">
                    User
                    <span class="sort-icon"></span>
                  </th>
                  <th @click="sortBy('session_type')" :class="{ sortable: true, sorted: sortKey === 'session_type' }">
                    Type
                    <span class="sort-icon"></span>
                  </th>
                  <th @click="sortBy('start_time')" :class="{ sortable: true, sorted: sortKey === 'start_time' }">
                    Schedule
                    <span class="sort-icon"></span>
                  </th>
                  <th @click="sortBy('session_duration')" :class="{ sortable: true, sorted: sortKey === 'session_duration' }">
                    Duration
                    <span class="sort-icon"></span>
                  </th>
                  <th @click="sortBy('price')" :class="{ sortable: true, sorted: sortKey === 'price' }">
                    Price
                    <span class="sort-icon"></span>
                  </th>
                  <th @click="sortBy('status')" :class="{ sortable: true, sorted: sortKey === 'status' }">
                    Status
                    <span class="sort-icon"></span>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading" class="loading-row">
                  <td colspan="8" class="loading-cell">
                    <div class="loading-content">
                      <div class="spinner-wrapper">
                        <svg class="spinner" viewBox="0 0 50 50">
                          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
                        </svg>
                      </div>
                      <span>Loading sessions...</span>
                    </div>
                  </td>
                </tr>
                <tr v-else v-for="session in filteredSessions" :key="session.id" class="session-row">
                  <td>{{ session.id }}</td>
                  <td>
                    <div class="user-info">
                      <div class="user-name">{{ session.user?.first_name }} {{ session.user?.last_name }}</div>
                      <div class="user-email">{{ session.user?.email }}</div>
                    </div>
                  </td>
                  <td>
                    <span class="type-badge">{{ session.session_type }}</span>
                  </td>
                  <td>
                    <div class="schedule-info">
                      <div class="time">{{ formatDateTime(session.start_time) }}</div>
                      <div class="timezone">{{ session.timezone || 'UTC' }}</div>
                    </div>
                  </td>
                  <td>
                    <div class="duration-badge">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="duration-icon">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      {{ session.session_duration }} min
                    </div>
                  </td>
                  <td>
                    <div class="price-info">
                      <span class="price">${{ session.price }}</span>
                    </div>
                  </td>
                  <td>
                    <span :class="['status-badge', session.status]">
                      <span class="status-dot"></span>
                      {{ session.status }}
                    </span>
                  </td>
                  <td>
                    <div class="action-buttons-stack">
                      <button @click="viewSession(session.id)" class="view-button">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="view-icon">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                        View
                      </button>
                      <button v-if="session.status === 'pending'" @click="updateStatus(session.id, 'confirmed')" class="confirm-button">
                        Confirm
                      </button>
                      <button v-if="session.status === 'pending'" @click="updateStatus(session.id, 'cancelled')" class="cancel-button">
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="!loading && pagination">
            <button 
              class="pagination-button" 
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="pagination-icon">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
              Previous
            </button>
            <div class="pagination-info">
              <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
              <span class="total-info">{{ filteredSessions.length }} sessions</span>
            </div>
            <button 
              class="pagination-button" 
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              Next
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="pagination-icon">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Sidebar from '@/components/Sidebar.vue'

export default {
  name: 'SessionsList',
  components: {
    Sidebar
  },
  data() {
    return {
      sessions: [],
      loading: true,
      error: null,
      currentPage: 1,
      totalPages: 1,
      pagination: null,
      isSidebarOpen: false,
      searchQuery: '',
      statistics: {
        total_sessions: 0,
        upcoming_sessions: 0,
        completed_sessions: 0,
        cancelled_sessions: 0,
        this_month_sessions: 0,
        average_session_price: '0'
      },
      sortKey: 'id',
      sortOrder: 'desc',
      filters: {
        status: '',
        type: '',
        duration: ''
      }
    }
  },
  computed: {
    filteredSessions() {
      let result = [...this.sessions]
      
      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.trim().toLowerCase()
        result = result.filter(session => {
          return (
            session.user?.first_name?.toLowerCase().includes(query) ||
            session.user?.last_name?.toLowerCase().includes(query) ||
            session.user?.email?.toLowerCase().includes(query)
          )
        })
      }
      
      // Apply status filter
      if (this.filters.status) {
        result = result.filter(session => session.status === this.filters.status)
      }
      
      // Apply type filter
      if (this.filters.type) {
        result = result.filter(session => session.session_type === this.filters.type)
      }
      
      // Apply duration filter
      if (this.filters.duration) {
        const duration = parseInt(this.filters.duration)
        result = result.filter(session => session.session_duration === duration)
      }
      
      // Apply sorting
      if (this.sortKey) {
        result.sort((a, b) => {
          let aValue = this.getSortValue(a, this.sortKey)
          let bValue = this.getSortValue(b, this.sortKey)
          
          if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase()
            bValue = bValue.toLowerCase()
          }
          
          if (aValue === bValue) return 0
          
          const comparison = aValue > bValue ? 1 : -1
          return this.sortOrder === 'desc' ? -comparison : comparison
        })
      }
      
      return result
    }
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen
    },
    async fetchSessions(page = 1) {
      try {
        this.loading = true
        const token = localStorage.getItem('token')
        const response = await axios.get(`https://api.kewaar.com/api/admin/sessions?page=${page}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.success) {
          this.sessions = response.data.data.data
          this.pagination = {
            total: response.data.data.total,
            per_page: response.data.data.per_page,
            current_page: response.data.data.current_page,
            last_page: response.data.data.last_page
          }
          this.currentPage = this.pagination.current_page
          this.totalPages = this.pagination.last_page
        }
      } catch (err) {
        this.error = 'Failed to load sessions: ' + err.message
      } finally {
        this.loading = false
      }
    },
    formatDateTime(datetime) {
      if (!datetime) return 'N/A'
      return new Date(datetime).toLocaleString()
    },
    changePage(page) {
      if (page !== this.currentPage) {
        this.currentPage = page
        this.fetchSessions(page)
      }
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortOrder = 'asc'
      }
    },
    getSortValue(session, key) {
      switch (key) {
        case 'user':
          return `${session.user?.first_name} ${session.user?.last_name}`
        case 'start_time':
          return session.start_time || ''
        default:
          return session[key] || ''
      }
    },
    async updateStatus(sessionId, newStatus) {
      try {
        const token = localStorage.getItem('token')
        await axios.put(`https://api.kewaar.com/api/admin/sessions/${sessionId}/status`, 
          { status: newStatus },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )
        
        // Refresh sessions after status update
        await this.fetchSessions(this.currentPage)
        
      } catch (error) {
        console.error('Error updating session status:', error)
      }
    },
    viewSession(id) {
      // Implement view session details
      console.log('Viewing session:', id)
    },
    async fetchStatistics() {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.get('https://api.kewaar.com/api/admin/sessions/statistics', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        })
        
        if (response.data.success) {
          // Map API response to our statistics structure
          this.statistics = {
            total_sessions: response.data.data.total_sessions || 0,
            upcoming_sessions: response.data.data.upcoming_sessions || 0,
            completed_sessions: response.data.data.completed_sessions || 0,
            cancelled_sessions: response.data.data.cancelled_sessions || 0,
            this_month_sessions: response.data.data.this_month_sessions || 0,
            average_session_price: response.data.data.average_session_price || '0'
          }
        }
      } catch (err) {
        console.error('Failed to load statistics:', err)
        // Set default values if API call fails
        this.statistics = {
          total_sessions: 0,
          upcoming_sessions: 0,
          completed_sessions: 0,
          cancelled_sessions: 0,
          this_month_sessions: 0,
          average_session_price: '0'
        }
      }
    }
  },
  created() {
    this.fetchSessions()
    this.fetchStatistics()
  }
}
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background-color: #F8FAFC;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
  max-width: 1920px;
}

.sessions-container {
  max-width: 100%;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2.5rem;
  background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
  border-radius: 20px;
  padding: 2.5rem;
  color: white;
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
  transform: translate(30%, -30%);
}

.header-content {
  position: relative;
  z-index: 1;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.stats-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  backdrop-filter: blur(8px);
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 1rem;
  max-width: 600px;
}

.statistics-section {
  margin-bottom: 2.5rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.stats-card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.stats-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1E293B;
  margin: 0;
}

.stats-icon {
  width: 32px;
  height: 32px;
  color: #6366F1;
}

.stats-content {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #64748B;
  font-weight: 500;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1E293B;
  margin-top: 0.25rem;
}

.search-and-filters {
  background: white;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.search-box {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #94A3B8;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1E293B;
  background-color: #F8FAFC;
}

.filter-controls {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-select {
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1E293B;
  background-color: #F8FAFC;
  cursor: pointer;
  min-width: 120px;
}

.content-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.table-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #E2E8F0;
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1E293B;
  margin: 0;
}

.sessions-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.sessions-table th {
  padding: 0.75rem 1.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748B;
  border-bottom: 1px solid #E2E8F0;
  background: #F8FAFC;
}

.sessions-table td {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #E2E8F0;
}

.session-row:hover td {
  background-color: #F1F5F9;
}

.type-badge {
  display: inline-flex;
  padding: 0.375rem 0.75rem;
  background: #EEF2FF;
  color: #6366F1;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: capitalize;
}

.schedule-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.schedule-info .time {
  font-weight: 500;
  color: #1E293B;
}

.schedule-info .timezone {
  font-size: 0.75rem;
  color: #64748B;
}

.duration-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748B;
  font-size: 0.875rem;
}

.duration-icon {
  width: 16px;
  height: 16px;
  color: #94A3B8;
}

.price-info {
  font-weight: 600;
  color: #059669;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.pending {
  background: #FEF3C7;
  color: #D97706;
}

.status-badge.confirmed {
  background: #ECFDF5;
  color: #059669;
}

.status-badge.cancelled {
  background: #FEF2F2;
  color: #DC2626;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.action-buttons-stack {
  display: flex;
  gap: 0.5rem;
}

.view-button,
.confirm-button,
.cancel-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.view-button {
  background: #EEF2FF;
  color: #6366F1;
}

.confirm-button {
  background: #059669;
  color: white;
}

.cancel-button {
  background: #FEF2F2;
  color: #DC2626;
}

.view-button:hover {
  background: #E0E7FF;
}

.confirm-button:hover {
  background: #047857;
}

.cancel-button:hover {
  background: #FEE2E2;
}

.loading-row {
  text-align: center;
}

.loading-cell {
  padding: 3rem !important;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #64748B;
}

.spinner-wrapper {
  width: 40px;
  height: 40px;
}

.spinner {
  animation: rotate 2s linear infinite;
}

.spinner .path {
  stroke: #6366F1;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid #E2E8F0;
}

.pagination-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  background: white;
  color: #1E293B;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background: #F8FAFC;
  border-color: #CBD5E0;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.page-info {
  font-size: 0.875rem;
  color: #64748B;
}

.total-info {
  font-size: 0.75rem;
  color: #94A3B8;
}

.pagination-icon {
  width: 16px;
  height: 16px;
}

@media (max-width: 1280px) {
  .main-content {
    padding: 1.5rem;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 1024px) {
  .main-content {
    margin-left: 0;
  }
  
  .page-header {
    padding: 2rem;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .search-and-filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    width: 100%;
  }
  
  .filter-controls {
    width: 100%;
  }
  
  .filter-select {
    flex: 1;
    min-width: 0;
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .action-buttons-stack {
    flex-direction: column;
  }
  
  .view-button,
  .confirm-button,
  .cancel-button {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 640px) {
  .page-header {
    padding: 1.5rem;
    border-radius: 16px;
  }
  
  .page-title {
    font-size: 1.75rem;
  }
  
  .table-responsive {
    overflow-x: auto;
  }
  
  .sessions-table {
    min-width: 800px;
  }
}
</style>